import { useCallback, useEffect, useId, useRef, useState, type FocusEvent } from "react";
import classNames from "classnames";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  COMMAND_PRIORITY_LOW,
  KEY_ENTER_COMMAND,
  KEY_ESCAPE_COMMAND,
  type EditorState,
  type LexicalEditor
} from "lexical";
import { MentionNode } from "@/lib/lexical/nodes/MentionNode";
import MentionPlugin from "@/lib/lexical/plugins/MentionPlugin";
import { hasOpenOverlay } from "@/hooks/useOverlayDismiss";
import useMentionSearch from "@/hooks/useMentionSearch";
import { serializeWithMentions, initEditorWithMentions } from "./mentionHelpers";
import type { MentionSearchHook } from "./types";

interface MentionableTextFieldProps {
  value?: string;
  onChange?: (serialized: string) => void;
  onEnter?: () => void;
  onFocus?: (event: FocusEvent<HTMLElement>) => void;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  placeholder?: string;
  label?: string;
  rows?: number;
  className?: string;
  overlayClassName?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  compact?: boolean;
  error?: boolean;
  errorText?: string;
  useSearch?: MentionSearchHook;
  allowSpacesInQuery?: boolean;
  "data-testid"?: string;
}

function SyncValuePlugin({
  value,
  lastEmittedRef,
  onPopulated
}: {
  value: string;
  lastEmittedRef: { current: string };
  onPopulated: (populated: boolean) => void;
}) {
  const [editor] = useLexicalComposerContext();
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      lastEmittedRef.current = value;
      if (value) {
        initEditorWithMentions(editor, value);
        onPopulated(true);
      }
      return;
    }
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    const rootEl = editor.getRootElement();
    const hadFocus = !!rootEl && rootEl === document.activeElement;
    initEditorWithMentions(editor, value);
    onPopulated(value.trim().length > 0);
    if (!hadFocus && rootEl) {
      queueMicrotask(() => {
        if (rootEl === document.activeElement) rootEl.blur();
      });
    }
  }, [editor, value, lastEmittedRef, onPopulated]);

  return null;
}

function EditablePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);
  return null;
}

function EnterSubmitPlugin({ onEnter }: { onEnter?: () => void }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!onEnter) return;

    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (event?.isComposing) return false;
        event?.preventDefault();
        onEnter();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, onEnter]);

  return null;
}

function EscapeBlurPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event) => {
        // Mention overlay registers Escape at HIGH while open. Dialog / SideSheet
        // / BottomDrawer own Escape via the overlay stack — do not preventDefault.
        if (hasOpenOverlay()) return false;
        event?.preventDefault();
        editor.getRootElement()?.blur();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor]);

  return null;
}

const mentionTheme = {
  paragraph: "m-0"
};

export default function MentionableTextField({
  value = "",
  onChange,
  onEnter,
  onFocus,
  onBlur,
  placeholder = "Add a comment...",
  label,
  rows = 1,
  className,
  overlayClassName,
  disabled = false,
  autoFocus = false,
  compact = false,
  error = false,
  errorText,
  useSearch = useMentionSearch,
  allowSpacesInQuery = false,
  "data-testid": testId = "mentionable-text-field"
}: MentionableTextFieldProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [isPopulated, setIsPopulated] = useState(!!value);
  const [mentionQuery, setMentionQuery] = useState("");
  const { users, isLoading, overlay, resolveSelect, dismiss, autoInsertUser } = useSearch(mentionQuery);
  const lastEmittedRef = useRef<string>(value);
  const errorMessageId = useId();

  const labelFloats = isFocused || isPopulated;
  const showPlaceholder = !isPopulated && (!label || isFocused);

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleChange = useCallback(
    (editorState: EditorState, _editor: LexicalEditor) => {
      const serialized = serializeWithMentions(editorState);
      lastEmittedRef.current = serialized;
      setIsPopulated(serialized.trim().length > 0);
      onChange?.(serialized);
    },
    [onChange]
  );

  const minHeight = Math.max(rows, 1) * 24;

  const editorConfig = {
    namespace: "MentionableTextField",
    theme: mentionTheme,
    editable: !disabled,
    onError: (error: Error) => {
      console.error("[MentionableTextField]", error);
    },
    nodes: [MentionNode]
  };

  return (
    <div className={classNames("relative", className)} data-testid={testId}>
      <LexicalComposer initialConfig={editorConfig}>
        <div className={classNames("relative rounded transition-colors", disabled && "opacity-50 pointer-events-none")}>
          <div
            className={classNames("pointer-events-none absolute inset-0 rounded transition-colors", {
              "border-2 border-sys-brand-error": error && isFocused,
              "border border-sys-brand-error": error && !isFocused,
              "border-2 border-sys-brand-primary": !error && isFocused,
              "border border-sys-brand-outline": !error && !isFocused
            })}
            aria-hidden="true"
          />

          {label && (
            <span
              className={classNames(
                "pointer-events-none absolute left-3 z-[2] transition-all duration-150",
                labelFloats
                  ? "text-body-small -top-2 leading-4 bg-sys-brand-surface px-1"
                  : "text-body-large top-4 leading-6 px-1",
                error
                  ? "text-sys-brand-error"
                  : isFocused
                    ? "text-sys-brand-primary"
                    : disabled
                      ? "text-sys-brand-on-surface/38"
                      : "text-sys-brand-on-surface-variant"
              )}
            >
              {label}
            </span>
          )}

          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={classNames(
                  "relative z-[1] text-sys-brand-on-surface outline-none overflow-y-auto",
                  compact ? "px-3 py-3 text-body-large" : "px-4 py-4 text-body-large"
                )}
                style={{ minHeight: `${minHeight}px` }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                aria-label={label || placeholder}
                aria-invalid={error || undefined}
                aria-describedby={error && errorText ? errorMessageId : undefined}
                data-testid={`${testId}-input`}
              />
            }
            placeholder={
              <div
                className={classNames(
                  "absolute pointer-events-none z-[1]",
                  compact ? "top-3 left-3 text-body-large" : "top-4 left-4 text-body-large",
                  compact && !label ? "text-sys-brand-outline" : "text-sys-brand-on-surface-variant",
                  showPlaceholder ? "opacity-100" : "opacity-0"
                )}
                aria-hidden={compact && !label ? true : undefined}
              >
                {showPlaceholder ? placeholder : ""}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <MentionPlugin
            users={users}
            isLoading={isLoading}
            onQueryChange={setMentionQuery}
            consumeEnterWhenOpen={!!onEnter}
            overlayClassName={overlayClassName}
            overlay={overlay}
            resolveSelect={resolveSelect}
            dismiss={dismiss}
            autoInsertUser={autoInsertUser}
            allowSpacesInQuery={allowSpacesInQuery}
          />
        </div>
        <EditablePlugin editable={!disabled} />
        <SyncValuePlugin value={value} lastEmittedRef={lastEmittedRef} onPopulated={setIsPopulated} />
        <EnterSubmitPlugin onEnter={onEnter} />
        <EscapeBlurPlugin />
        {autoFocus && <AutoFocusPlugin defaultSelection="rootEnd" />}
        <HistoryPlugin />
        <OnChangePlugin onChange={handleChange} />
      </LexicalComposer>
      {error && errorText ? (
        <p
          id={errorMessageId}
          className="mt-1 px-1 text-body-small text-sys-brand-error"
          data-testid={`${testId}-error`}
        >
          {errorText}
        </p>
      ) : null}
    </div>
  );
}
